import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    InputNumber,
    Select,
    Radio,
    DatePicker,
    TimePicker,
    Space,
    Typography,
    Divider,
    Button
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);


const { Text } = Typography;
const { Option } = Select;

export interface RecurrenceRule {
    interval: number;
    frequency: 'day' | 'week' | 'month' | 'year';
    hour?: string;
    startsFrom?: string;
    ends: {
        type: 'never' | 'on_date' | 'after_occurrences';
        value?: any;
    };
}

interface NotificationRecurrenceModalProps {
    open: boolean;
    onCancel: () => void;
    initialRule?: RecurrenceRule | null;
    defaultScheduledAt?: Dayjs | null;
    onSave: (rule: RecurrenceRule | null) => void;
}

export const NotificationRecurrenceModal: React.FC<NotificationRecurrenceModalProps> = ({
    open,
    onCancel,
    initialRule,
    defaultScheduledAt,
    onSave
}) => {
    const [form] = Form.useForm();
    const [endType, setEndType] = useState<'never' | 'on_date' | 'after_occurrences'>(initialRule?.ends?.type || 'never');

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                interval: initialRule?.interval || 1,
                frequency: initialRule?.frequency || 'day',
                hour: initialRule?.hour ? dayjs(`2000-01-01 ${initialRule.hour}`) : (defaultScheduledAt || null),
                startsFrom: initialRule?.startsFrom ? dayjs(initialRule.startsFrom) : (defaultScheduledAt || dayjs()),
                endType: initialRule?.ends?.type || 'never',
                endDate: initialRule?.ends?.type === 'on_date' ? dayjs(initialRule.ends.value) : null,
                occurrences: initialRule?.ends?.type === 'after_occurrences' ? initialRule.ends.value : 10,
            });
            setEndType(initialRule?.ends?.type || 'never');
        }
    }, [open, initialRule, defaultScheduledAt, form]);

    const handleOk = () => {
        form.validateFields().then(values => {
            const rule: RecurrenceRule = {
                interval: values.interval,
                frequency: values.frequency,
                hour: values.hour ? values.hour.format('HH:mm') : undefined,
                startsFrom: values.startsFrom ? values.startsFrom.toISOString() : undefined,
                ends: {
                    type: values.endType,
                    value: values.endType === 'on_date' ? values.endDate?.toISOString() : (values.endType === 'after_occurrences' ? values.occurrences : undefined)
                }
            };
            onSave(rule);
            onCancel();
        });
    };

    const handleClear = () => {
        onSave(null);
        onCancel();
    };

    return (
        <Modal
            title="Configurar Repetición"
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            footer={[
                <Button key="clear" danger onClick={handleClear} style={{ float: 'left' }}>
                    Quitar repetición
                </Button>,
                <Button key="cancel" onClick={onCancel}>
                    Cancelar
                </Button>,
                <Button key="save" type="primary" onClick={handleOk}>
                    Listo
                </Button>,
            ]}
            width={400}
            centered
        >
            <Form form={form} layout="vertical" initialValues={{ interval: 1, frequency: 'day', endType: 'never' }}>
                <Form.Item label="Repetir cada" required>
                    <Space>
                        <Form.Item name="interval" noStyle>
                            <InputNumber min={1} style={{ width: 60 }} />
                        </Form.Item>
                        <Form.Item name="frequency" noStyle>
                            <Select style={{ width: 120 }}>
                                <Option value="day">Días</Option>
                                <Option value="week">Semanas</Option>
                                <Option value="month">Meses</Option>
                                <Option value="year">Años</Option>
                            </Select>
                        </Form.Item>
                    </Space>
                </Form.Item>

                <Form.Item name="startsFrom" label="Empieza el">
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="hour" label="Hora">
                    <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>

                <Divider orientation="left" plain>Termina</Divider>

                <Form.Item name="endType" noStyle>
                    <Radio.Group onChange={(e) => setEndType(e.target.value)} style={{ width: '100%' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Radio value="never">Nunca</Radio>
                            
                            <Space align="center">
                                <Radio value="on_date">El día</Radio>
                                <Form.Item name="endDate" noStyle>
                                    <DatePicker disabled={endType !== 'on_date'} format="YYYY-MM-DD" style={{ width: 140 }} />
                                </Form.Item>
                            </Space>

                            <Space align="center">
                                <Radio value="after_occurrences">Después de</Radio>
                                <Form.Item name="occurrences" noStyle>
                                    <InputNumber disabled={endType !== 'after_occurrences'} min={1} style={{ width: 60 }} />
                                </Form.Item>
                                <Text disabled={endType !== 'after_occurrences'}>repeticiones</Text>
                            </Space>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
};
